import React, { useEffect, useState } from "react";
import { ProductCategory } from "../context/ShoppingCartContext";
import usePageLanguage from "../hooks/usePageLanguage";
import "./Category.css";
import { useLocation } from "react-router-dom";
import { useAppSelector } from "../../../store/hooks";
import {
  categoryContainsDescendant,
  getCategoryChildren,
} from "./categoryTree";

interface Props {
  productCategory: ProductCategory;
  productCategoryUuid: string | null;
  setProductCategoryUuid: (val: string) => void;
  depthLevel?: number;
  productCategories: ProductCategory[];
}

function CategoryComponent({
  productCategory,
  productCategoryUuid,
  setProductCategoryUuid,
  depthLevel = 0,
  productCategories,
}: Props) {
  const [open, setOpen] = useState<boolean>(false);
  const lang = usePageLanguage();
  const location = useLocation();

  const [isHovered, setIsHovered] = useState(false);

  const typographies = useAppSelector((state) => state.builder.typographies);
  const colors = useAppSelector((state) => state.builder.colors);
  const childCategories = getCategoryChildren(productCategory);

  const filteredTypography = typographies.filter((x) => {
    if (
      x.activeComponents &&
      x.activeComponents.includes("productsCategory")
    ) {
      return x;
    } else {
      return null;
    }
  });

  const typographyStyles =
    filteredTypography.length > 0
      ? {
          fontFamily: filteredTypography[0].fontFamily,
          fontSize:
            filteredTypography[0].fontSize +
            filteredTypography[0].fontSizeUnit,
          fontStyle: filteredTypography[0].fontStyle,
          fontWeight: filteredTypography[0].fontWeight,
          letterSpacing: filteredTypography[0].letterSpacing,
          lineHeight: filteredTypography[0].lineHeight,
          color: colors.find((x) => x.id === filteredTypography[0].colorId)
            ?.color,

          ...(productCategoryUuid === productCategory.uuid && {
            color: colors.find(
              (x) => x.id === filteredTypography[0].linkHoverColorId
            )?.color,
          }),

          ...(isHovered && {
            color: colors.find(
              (x) => x.id === filteredTypography[0].linkHoverColorId
            )?.color,
          }),
        }
      : {};

  useEffect(() => {
    if (depthLevel === 0) {
      setOpen(false);
    } else if (depthLevel === 1) {
      setOpen(false);
      const categoryFound = productCategories.find(
        (pc) => pc.uuid === productCategory.uuid
      );

      if (categoryFound) {
        setOpen(true);
      }
    } else if (depthLevel === 2) {
      productCategories.forEach((pc) => {
        if (pc.uuid === productCategory.uuid) {
          setOpen(true);
        } else {
          getCategoryChildren(pc).forEach((child) => {
            if (child.uuid === productCategory.uuid) {
              setOpen(true);
            }
          });
        }
      });
    } else if (depthLevel === 3) {
      setOpen(true);
    }
  }, [depthLevel, productCategories, productCategory.uuid]);

  const onCategoryClick = (value: string) => {
    setProductCategoryUuid(value);
    setOpen(!open);
  };

  useEffect(() => {
    const categoryUuidFromUrl = location.search.split("?category=")[1];
    if (!categoryUuidFromUrl) {
      return;
    }

    if (productCategory.uuid === categoryUuidFromUrl) {
      setProductCategoryUuid(categoryUuidFromUrl);
      setOpen(true);
      return;
    }

    if (categoryContainsDescendant(productCategory, categoryUuidFromUrl)) {
      setProductCategoryUuid(categoryUuidFromUrl);
      setOpen(true);
    }
  }, []);

  return (
    <div className="categoryDropdown categoryItem">
      <div style={{ display: "flex", gap: "4px" }}>
        <a
          onClick={() => {
            onCategoryClick(productCategory.uuid);
          }}
          key={productCategory.uuid}
          className={`category-name collapsed${
            productCategoryUuid === productCategory.uuid ? " active" : ""
          }`}
          data-toggle="collapse"
          data-value={productCategory.uuid}
          aria-expanded={open ? "true" : "false"}
          role="button"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          style={typographyStyles}
        >
          {productCategory.translations[lang]?.name}
        </a>
      </div>
      {childCategories.length > 0 && (
        <div className={`collapse${open ? " show" : ""} panel-container`}>
          {childCategories.map((childCategory) => (
            <div
              key={childCategory.uuid}
              id={childCategory.uuid}
              role="menu"
            >
              <div className="">
                <CategoryComponent
                  productCategories={productCategories}
                  depthLevel={depthLevel}
                  productCategory={childCategory}
                  productCategoryUuid={productCategoryUuid}
                  setProductCategoryUuid={setProductCategoryUuid}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default React.memo(CategoryComponent);
